import { FilterConfiguration, FilterGroup, FilterField } from '../types';

/**
 * Get nested property value from an object using dot notation path
 * Examples:
 * - "firstName" returns item.firstName
 * - "trackingParameter.utmCampaign" returns item.trackingParameter.utmCampaign
 * - "caregiver.email" returns item.caregiver.email
 *
 * @param obj The object to get the value from
 * @param path The dot-notation path to the property
 * @returns The value at the path, or undefined if not found
 */
export const getNestedProperty = (obj: any, path: string): any => {
  if (!obj || !path) return undefined;

  // Split the path by dots
  const keys = path.split('.');

  // Navigate through the object
  let current = obj;
  for (const key of keys) {
    if (current === null || current === undefined) {
      return undefined;
    }
    current = current[key];
  }

  return current;
};

/**
 * Dynamically extract all filter field keys from Sitecore configuration
 * This replaces the static FilterValues interface with dynamic keys
 */
export const getFilterKeysFromConfig = (filterConfig?: FilterConfiguration): string[] => {
  if (!filterConfig?.filterGroups || !filterConfig.enabled) {
    return [];
  }

  const keys = new Set<string>();

  filterConfig.filterGroups.forEach((group: FilterGroup) => {
    if (!group.enabled) return;

    // Extract keys from regular fields
    if (group.fields && group.type !== 'checkbox-card') {
      group.fields.forEach((field: FilterField) => {
        if (!field.enabled) return;

        // For slider fields, add both min and max fields
        if (field.type === 'slider' && 'fields' in field) {
          const sliderField = field as any;
          if (sliderField.fields?.minField) {
            keys.add(sliderField.fields.minField);
          }
          if (sliderField.fields?.maxField) {
            keys.add(sliderField.fields.maxField);
          }
        }
        // For date range fields
        else if (field.type === 'dateRange') {
          keys.add('dateFrom');
          keys.add('dateTo');
        }
        // For regular fields, use the field property or id
        else {
          const fieldKey = field.field || field.id;
          keys.add(fieldKey);
        }
      });
    }

    // Extract keys from checkbox groups
    if (group.checkboxGroups && group.type === 'checkbox-card') {
      group.checkboxGroups.forEach((checkboxGroup) => {
        if (!checkboxGroup.enabled) return;
        if (checkboxGroup.field) {
          keys.add(checkboxGroup.field);
        }
      });
    }
  });

  return Array.from(keys);
};

/**
 * Create initial filter state based on Sitecore configuration
 * Returns an object with all filter keys initialized to empty strings
 */
export const getInitialFiltersFromConfig = (
  filterConfig?: FilterConfiguration
): Record<string, string> => {
  const keys = getFilterKeysFromConfig(filterConfig);
  const initialFilters: Record<string, string> = {};

  keys.forEach((key) => {
    initialFilters[key] = '';
  });

  return initialFilters;
};

/**
 * Type guard to ensure a key exists in the dynamic filters object
 */
export const isValidFilterKey = (
  key: string,
  filters: Record<string, string>
): key is keyof typeof filters => {
  return key in filters;
};
